import React, { useState, useEffect } from 'react';
import { X, Edit, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface Objective {
  id: string;
  employeeName: string;
  employeeAvatar?: string;
  linkedRole: string;
  department: string;
  focus: string;
  horizon: string;
  supporter: string;
  status: string;
}

interface EditObjectiveModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: Objective) => void;
  objective: Objective | null;
}

interface FormData {
  focus: string;
  horizon: string;
  supporter: string;
  status: string;
}

interface SelectOption {
  value: string;
  label: string;
}

const EditObjectiveModal: React.FC<EditObjectiveModalProps> = ({ isOpen, onClose, onSave, objective }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<FormData>({
    focus: '',
    horizon: '3 months',
    supporter: '',
    status: 'Active'
  });

  const [errors, setErrors] = useState<Record<string, string | null>>({});

  useEffect(() => {
    if (objective) {
      setFormData({
        focus: objective?.focus || '',
        horizon: objective?.horizon || '3 months',
        supporter: objective?.supporter || '',
        status: objective?.status || 'Active'
      });
    }
  }, [objective]);

  const horizonOptions: SelectOption[] = [
    { value: '3 months', label: t('3 Months') },
    { value: '6 months', label: t('6 Months') }
  ];

  const supporterOptions: SelectOption[] = [
    { value: 'Michael Chen', label: 'Michael Chen - Product Director' },
    { value: 'Emily Rodriguez', label: 'Emily Rodriguez - Analytics Lead' },
    { value: 'David Thompson', label: 'David Thompson - Design Manager' },
    { value: 'Jessica Lee', label: 'Jessica Lee - UX Research Lead' }
  ];

  const statusOptions: SelectOption[] = [
    { value: 'Active', label: t('Active') },
    { value: 'Completed', label: t('Completed') },
    { value: 'Archived', label: t('Archived') },
    { value: 'Pending', label: t('Pending') }
  ];

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const newErrors: Record<string, string | null> = {};
    if (!formData?.focus?.trim()) {
      newErrors.focus = t('Development focus is required');
    } else if (formData?.focus?.trim()?.length > 120) {
      newErrors.focus = t('Focus must be 120 characters or less');
    }
    if (!formData?.supporter) {
      newErrors.supporter = t('Supporter is required');
    }
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSave = () => {
    if (validate()) {
      onSave?.({
        ...objective!,
        ...formData
      });
      onClose?.();
    }
  };

  if (!isOpen) return null;

  const focusCharCount = formData?.focus?.length || 0;
  const isOverLimit = focusCharCount > 120;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
                <Edit className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">{t('Edit Development Objective')}</h2>
                <p className="text-sm text-gray-600">{t('Update objective details for')} {objective?.employeeName}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-900 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          <div className="space-y-6">
            {/* Employee Info (Read-only) */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center space-x-3">
                {objective?.employeeAvatar ? (
                  <img
                    src={objective?.employeeAvatar}
                    alt={`${objective?.employeeName} profile photo`}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-12 h-12 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center text-white font-medium">
                    {objective?.employeeName?.split(' ')?.map(n => n?.[0])?.join('')}
                  </div>
                )}
                <div>
                  <p className="text-sm font-semibold text-gray-900">{objective?.employeeName}</p>
                  <p className="text-xs text-gray-600">{objective?.linkedRole} • {objective?.department}</p>
                </div>
              </div>
            </div>

            {/* Focus Field */}
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                {t('Development Focus')} <span className="text-red-500">*</span>
              </label>
              <textarea
                value={formData?.focus}
                onChange={(e) => handleChange('focus', e?.target?.value)}
                placeholder={t('What capability or skill area will this person develop?')}
                className={`w-full h-32 px-4 py-3 border rounded-lg bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none ${
                  errors?.focus || isOverLimit ? 'border-red-500' : 'border-border'
                }`}
                maxLength={150}
              />
              <div className="flex items-center justify-between mt-2">
                {errors?.focus ? (
                  <p className="text-xs text-red-600">{errors?.focus}</p>
                ) : (
                  <p className="text-xs text-gray-600">{t('Be specific about the capability being developed')}</p>
                )}
                <p
                  className={`text-xs font-medium ${
                    isOverLimit ? 'text-red-600' : focusCharCount > 100 ? 'text-yellow-600' : 'text-gray-600'
                  }`}
                >
                  {focusCharCount} / 120
                </p>
              </div>
            </div>

            {/* Horizon */}
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                {t('Development Horizon')} <span className="text-red-500">*</span>
              </label>
              <select
                value={formData?.horizon}
                onChange={(e) => handleChange('horizon', e?.target?.value)}
                className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                {horizonOptions?.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>

            {/* Supporter */}
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                {t('Supporter')} <span className="text-red-500">*</span>
              </label>
              <select
                value={formData?.supporter}
                onChange={(e) => handleChange('supporter', e?.target?.value)}
                className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="">{t('Select supporter...')}</option>
                {supporterOptions?.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
              {errors?.supporter && <p className="text-xs text-red-600 mt-2">{errors?.supporter}</p>}
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                {t('Status')} <span className="text-red-500">*</span>
              </label>
              <select
                value={formData?.status}
                onChange={(e) => handleChange('status', e?.target?.value)}
                className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                {statusOptions?.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-border flex items-center justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-border rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
          >
            {t('Cancel')}
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-primary text-white rounded-md text-sm font-medium hover:bg-primary/90 transition-colors flex items-center"
          >
            <Check className="w-4 h-4 mr-2" />
            {t('Save Changes')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditObjectiveModal;
